import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectTasks() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [tasks, setTasks] = useState([]);
  const [taskGroups, setTaskGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [filters, setFilters] = useState({ status: '', priority: '', task_group_id: '', search: '', root_only: true });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', task_group_id: '', assigned_user_id: '', priority: 'normal', status: 'to_do', due_date: '', description: '', estimated_effort_hours: '', parent_task_id: '' });
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newChecklist, setNewChecklist] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [pagination, setPagination] = useState({});

  const loadTasks = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== false) params.append(k, v); });
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setTasks(Array.isArray(d.data) ? d.data : []); setPagination(d); })
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [companyId, projectId, API, filters]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    if (!companyId || !projectId) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/task-groups`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTaskGroups(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/projects/${projectId}/team?active_only=1`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTeamMembers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, projectId, API]);

  const loadDetail = (taskId) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => { setDetail(d); setShowDetail(true); }).catch(() => {});
  };

  const handleSave = () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API}/companies/${companyId}/projects/${projectId}/tasks/${editId}` : `${API}/companies/${companyId}/projects/${projectId}/tasks`;
    const body = { ...form };
    if (!body.task_group_id) delete body.task_group_id;
    if (!body.assigned_user_id) delete body.assigned_user_id;
    if (!body.parent_task_id) delete body.parent_task_id;
    if (!body.estimated_effort_hours) delete body.estimated_effort_hours;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowModal(false); setEditId(null); resetForm(); loadTasks(); })
      .catch(() => window.alert('Failed to save task'));
  };

  const resetForm = () => setForm({ name: '', task_group_id: '', assigned_user_id: '', priority: 'normal', status: 'to_do', due_date: '', description: '', estimated_effort_hours: '', parent_task_id: '' });

  const changeStatus = (taskId, status) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status })
    }).then(r => r.ok ? r.json() : null).then(() => { loadTasks(); if (detail && detail.id === taskId) loadDetail(taskId); }).catch(() => {});
  };

  const addComment = (taskId) => {
    if (!newComment.trim()) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/comments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: newComment })
    }).then(r => r.ok ? r.json() : null).then(() => { setNewComment(''); loadDetail(taskId); }).catch(() => {});
  };

  const addChecklistItem = (taskId) => {
    if (!newChecklist.trim()) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/checklists`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ item: newChecklist })
    }).then(r => r.ok ? r.json() : null).then(() => { setNewChecklist(''); loadDetail(taskId); }).catch(() => {});
  };

  const toggleChecklist = (taskId, checklistId) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/checklists/${checklistId}/toggle`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(r => r.ok ? r.json() : null).then(() => loadDetail(taskId)).catch(() => {});
  };

  const startTimer = (taskId) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/timer/start`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(r => r.ok ? r.json() : null).then(() => window.alert('Timer started!')).catch(() => window.alert('Failed to start timer'));
  };

  const statusColor = { to_do: '#94a3b8', in_progress: '#3b82f6', blocked: '#ef4444', in_review: '#f59e0b', completed: '#22c55e', cancelled: '#6b7280' };
  const priorityColor = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', critical: '#ef4444' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>📋 Tasks & Subtasks</h2>
        </div>
        <button onClick={() => { setEditId(null); resetForm(); setShowModal(true); }} style={primaryBtn}>+ New Task</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input placeholder="Search..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} style={inputStyle} />
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} style={selectStyle}>
          <option value="">All Status</option>
          {['to_do','in_progress','blocked','in_review','completed','cancelled'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })} style={selectStyle}>
          <option value="">All Priority</option>
          {['low','normal','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.task_group_id} onChange={e => setFilters({ ...filters, task_group_id: e.target.value })} style={selectStyle}>
          <option value="">All Groups</option>
          {taskGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {/* Tasks Table */}
      {initialLoading ? <p>Loading...</p> : (
        <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Task</th>
                <th style={thStyle}>Assignee</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Priority</th>
                <th style={thStyle}>Due Date</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Group</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => loadDetail(t.id)}>{t.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{t.reference} {t.subtasks_count > 0 && `(${t.subtasks_count} subtasks)`}</div>
                  </td>
                  <td style={tdStyle}>{t.assigned_user?.name || '-'}</td>
                  <td style={tdStyle}><span style={{ ...badge, backgroundColor: statusColor[t.status] || '#6b7280' }}>{(t.status || '').replace(/_/g,' ')}</span></td>
                  <td style={tdStyle}><span style={{ ...badge, backgroundColor: priorityColor[t.priority] || '#6b7280' }}>{t.priority}</span></td>
                  <td style={{ ...tdStyle, color: t.due_date && new Date(t.due_date) < new Date() && !['completed','cancelled'].includes(t.status) ? '#ef4444' : 'inherit' }}>{t.due_date || '-'}</td>
                  <td style={tdStyle}>{t.progress_percentage || 0}%</td>
                  <td style={tdStyle}>{t.task_group?.name || '-'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {t.status !== 'completed' && t.status !== 'cancelled' && (
                        <>
                          <button onClick={() => changeStatus(t.id, 'completed')} style={smallBtn} title="Complete">✓</button>
                          <button onClick={() => startTimer(t.id)} style={smallBtn} title="Start Timer">⏱</button>
                        </>
                      )}
                      <button onClick={() => { setEditId(t.id); setForm({ name: t.name, task_group_id: t.task_group_id || '', assigned_user_id: t.assigned_user_id || '', priority: t.priority, status: t.status, due_date: t.due_date || '', description: t.description || '', estimated_effort_hours: t.estimated_effort_hours || '', parent_task_id: t.parent_task_id || '' }); setShowModal(true); }} style={smallBtn}>✏️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && <tr><td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No tasks found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{editId ? 'Edit Task' : 'New Task'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Task Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
              <select value={form.task_group_id} onChange={e => setForm({ ...form, task_group_id: e.target.value })} style={selectStyle}>
                <option value="">No Group</option>
                {taskGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <select value={form.assigned_user_id} onChange={e => setForm({ ...form, assigned_user_id: e.target.value })} style={selectStyle}>
                <option value="">Unassigned</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.user?.name || `User #${m.user_id}`}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ ...selectStyle, flex: 1 }}>
                  {['low','normal','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input type="date" placeholder="Due Date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
              </div>
              <input type="number" placeholder="Estimated Hours" value={form.estimated_effort_hours} onChange={e => setForm({ ...form, estimated_effort_hours: e.target.value })} style={inputStyle} />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      {showDetail && detail && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '650px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{detail.name}</h3>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{detail.reference}</span>
              </div>
              <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ ...badge, backgroundColor: statusColor[detail.status] || '#6b7280' }}>{(detail.status || '').replace(/_/g,' ')}</span>
              <span style={{ ...badge, backgroundColor: priorityColor[detail.priority] || '#6b7280' }}>{detail.priority}</span>
            </div>
            {detail.description && <p style={{ fontSize: '0.85rem', margin: '0 0 12px', color: '#475569' }}>{detail.description}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px', fontSize: '0.82rem' }}>
              <div><strong>Assignee:</strong> {detail.assigned_user?.name || '-'}</div>
              <div><strong>Due:</strong> {detail.due_date || '-'}</div>
              <div><strong>Milestone:</strong> {detail.milestone?.name || '-'}</div>
              <div><strong>Deliverable:</strong> {detail.deliverable?.name || '-'}</div>
              <div><strong>Progress:</strong> {detail.progress_percentage || 0}%</div>
              <div><strong>Est. Hours:</strong> {detail.estimated_effort_hours || '-'}</div>
            </div>

            {/* Quick Status Actions */}
            {!['completed','cancelled'].includes(detail.status) && (
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {['to_do','in_progress','blocked','in_review','completed'].map(s => s !== detail.status && (
                  <button key={s} onClick={() => changeStatus(detail.id, s)} style={{ ...smallBtn, fontSize: '0.75rem' }}>{s.replace(/_/g,' ')}</button>
                ))}
              </div>
            )}

            {/* Subtasks */}
            {Array.isArray(detail.subtasks) && detail.subtasks.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Subtasks ({detail.subtasks.length})</h4>
                {detail.subtasks.map(st => (
                  <div key={st.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                    <span>{st.name}</span>
                    <span style={{ ...badge, backgroundColor: statusColor[st.status] || '#6b7280', fontSize: '0.65rem' }}>{st.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Checklist */}
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Checklist</h4>
              {Array.isArray(detail.checklists) && detail.checklists.map(cl => (
                <div key={cl.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', fontSize: '0.82rem' }}>
                  <input type="checkbox" checked={cl.is_completed} onChange={() => toggleChecklist(detail.id, cl.id)} />
                  <span style={{ textDecoration: cl.is_completed ? 'line-through' : 'none', color: cl.is_completed ? '#94a3b8' : '#334155' }}>{cl.item}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                <input placeholder="Add item..." value={newChecklist} onChange={e => setNewChecklist(e.target.value)} style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addChecklistItem(detail.id)} />
                <button onClick={() => addChecklistItem(detail.id)} style={smallBtn}>Add</button>
              </div>
            </div>

            {/* Comments */}
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Comments</h4>
              {Array.isArray(detail.comments) && detail.comments.map(c => (
                <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                  <strong>{c.user?.name || 'Unknown'}</strong> <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{new Date(c.created_at).toLocaleString()}</span>
                  <div style={{ marginTop: '2px' }}>{c.comment}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <input placeholder="Add comment..." value={newComment} onChange={e => setNewComment(e.target.value)} style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addComment(detail.id)} />
                <button onClick={() => addComment(detail.id)} style={smallBtn}>Post</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', backgroundColor: '#fff' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
